import { GoogleMap, Marker } from '@react-google-maps/api';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { ReactComponent as LocationIcon } from '../../assets/icons/location-icon.svg';
import { useAppBranding } from '../../config/branding';
import { useGoogleMaps } from '../../contexts/google-map-context/GoogleMapContent';
import { ChargingStationProperties } from '../../interface/facility-side-panel.interface';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  fetchFacilityAccountDetails,
  resetFacilitySidePanel,
  setFacilityLimit,
  setFacilitySortData,
} from '../../redux/slices/FacilitySidePanelSlice';
import FormatUtcString from '../../shared-functions/FormatUtcString';
import CustomTable from '../custom-table/CustomTable';

interface FacilitySidePanelProperties {
  fetchIdFrom: 'queryParams' | 'props';
  id?: string;
}

/**
 * check the lat and lng is valid or not
 * @param position
 * @returns
 */
const validatePosition = (position: { latitude: any; longitude: any }) => {
  const lat = parseFloat(position.latitude);
  const lng = parseFloat(position.longitude);
  return !Number.isNaN(lat) && !Number.isNaN(lng) ? { lat, lng } : null;
};

/**
 * Facility side panel view
 * @returns
 */
export default function FacilitySidePanel(properties: FacilitySidePanelProperties) {
  const { t } = useTranslation('sidePanel');
  const { fetchIdFrom, id } = properties;
  const dispatch = useAppDispatch();
  const branding = useAppBranding();

  const { accountData, loading, limit, page, totalPages, totalResults, sortBy, sortField } =
    useAppSelector((state) => state.facilitySidePanel);

  const { isLoaded } = useGoogleMaps();
  const mapReference = useRef<google.maps.Map | null>(null);

  const [searchParameters] = useSearchParams();

  const stationMarkers =
    accountData?.chargingStationData?.map((chargingStation: any) => ({
      latitude: Number(chargingStation.latitude),
      longitude: Number(chargingStation.longitude),
    })) || [];

  const column = [
    {
      label: t('stationName'),
      accessorKey: 'name' as keyof ChargingStationProperties,
      sortAllow: true,
    },
    {
      label: t('operatingHrs'),
      accessorKey: 'isDeviceOffline' as keyof ChargingStationProperties,
      sortAllow: true,
      /**
       * Time view in table
       * @param station
       * @param index
       */
      customData: (station: ChargingStationProperties) =>
        station.opensAt && station.closesAt ? (
          <p className="table-row-each-data">{`${FormatUtcString(station.opensAt)} - ${FormatUtcString(station.closesAt)}`}</p>
        ) : null,
    },
    {
      label: t('location'),
      accessorKey: 'address' as keyof ChargingStationProperties,
      sortAllow: true,
      /**
       * Location view in table
       * @param station
       * @param index
       */
      customData: (station: ChargingStationProperties) =>
        station.address ? (
          <div className="location-in-station-table">
            <span>
              <LocationIcon />
            </span>
            <p className="table-row-each-data">{station.address}</p>
          </div>
        ) : null,
    },
  ];
  /**
   * Function to handle page limit change
   * @param value
   */
  const pageNateLimitChange = (value: any) => {
    let accountId;
    if (fetchIdFrom === 'queryParams') {
      accountId = searchParameters.get('deviceId');
    } else if (fetchIdFrom === 'props') {
      accountId = id;
    }

    if (!accountId) {
      return;
    }
    dispatch(setFacilityLimit(value));
    //   dispatch(setFacilityPage(1));
    fetchFacilityAccountDetails({
      id: accountId,
      ...value,
      sortBy,
      sortField,
    });
  };
  /**
   * Function to handle sort change
   * @param value
   */
  const handleSortFunction = (value: any) => {
    let accountId;
    if (fetchIdFrom === 'queryParams') {
      accountId = searchParameters.get('deviceId');
    } else if (fetchIdFrom === 'props') {
      accountId = id;
    }

    if (!accountId) {
      return;
    }
    dispatch(setFacilitySortData(value));
    fetchFacilityAccountDetails({
      id: accountId,
      ...value,
    });
  };
  /**
   * Function to fetch station details
   * @param accountId
   */
  const handleFacilityAccountDetails = (accountId: string) => {
    dispatch(
      fetchFacilityAccountDetails({
        id: accountId,
        page,
        limit,
        sortBy,
        sortField,
      }),
    );
  };

  useEffect(() => {
    let accountId;
    if (fetchIdFrom === 'queryParams') {
      accountId = searchParameters.get('deviceId');
    } else if (fetchIdFrom === 'props') {
      accountId = id;
    }

    if (!accountId) {
      return;
    }

    handleFacilityAccountDetails(accountId);
  }, [searchParameters, id, page, limit, sortBy, sortField]);

  useEffect(() => {
    return () => {
      dispatch(resetFacilitySidePanel());
    };
  }, []);

  return (
    <div className="facility-side-panel-wrap">
      <div className="facility-side-panel-details">
        <h2>{accountData?.accountName}</h2>
        {/* Need a confirmation for admin user name */}
        {/* <p>Admin: Jackson</p> */}
      </div>
      <div className="facility-station-wrap">
        <h6>{t('stations')}</h6>
        <div className="facility-station-map-container">
          {isLoaded ? (
            <GoogleMap
              mapContainerStyle={{
                width: '100%',
                height: '100%',
              }}
              center={{
                lat: Number(stationMarkers?.[0]?.latitude) || 0,
                lng: Number(stationMarkers?.[0]?.longitude) || 0,
              }}
              zoom={Number(stationMarkers?.[0]?.latitude) ? 3 : 1}
              onLoad={(map) => {
                mapReference.current = map; // Assign the map instance to the reference
              }}>
              {stationMarkers &&
                stationMarkers.map((eachMarker, index) =>
                  (() => {
                    const validPosition = validatePosition({
                      latitude: eachMarker.latitude,
                      longitude: eachMarker.longitude,
                    });
                    return validPosition ? (
                      <Marker
                        key={index}
                        position={validPosition}
                        // clusterer={clusterer}
                        icon={{
                          url: branding.icons.stationLocator,
                          scaledSize: new window.google.maps.Size(44, 44), // Width, Height
                        }}
                      />
                    ) : null;
                  })(),
                )}
            </GoogleMap>
          ) : null}
        </div>
      </div>
      <div className="facility-station-table-wrap">
        <CustomTable
          header={column}
          data={accountData?.chargingStationData}
          serialNo
          actionButton={false}
          limit={limit}
          page={page}
          loading={loading}
          totalPages={totalPages}
          totalResults={totalResults}
          sortData={{ sortBy, sortField }}
          pageLimitChange={pageNateLimitChange}
          handleSortChange={handleSortFunction}
          sizeChangeButtonRequired
          disablePagination={false}
          disableHeader
        />
      </div>
    </div>
  );
}
